import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { dataService } from '@/services/dataService';
import { Automation } from '@/types/entities';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { useI18n } from '@/i18n';
import { useToast } from '@/components/ui/Toast';
import { AutomationFormDrawer, AutomationFormValues } from './AutomationFormDrawer';
import { getDepartmentDefinition } from '@/config/agentDepartments.config';
import { DemoSettings } from '@/services/demoDataService';

/**
 * AUTOMATIONS — local Preview definitions only (Phase D completion). No
 * execution backend/scheduler exists, so status is always one of
 * draft/waiting_connector/paused/archived — never 'active'. Full CRUD
 * proxies to dataService, same as Agents/Creators.
 */
export function AutomationsPage() {
  const { t } = useI18n();
  const push = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [settings, setSettings] = useState<DemoSettings | null>(null);
  const [formOpen, setFormOpen] = useState(searchParams.get('new') === '1');
  const [editTarget, setEditTarget] = useState<Automation | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Automation | null>(null);

  function reload() {
    dataService.listAutomations().then(setAutomations);
    dataService.getSettings().then(setSettings);
  }
  useEffect(reload, []);

  async function handleCreate(values: AutomationFormValues) {
    const created = await dataService.createAutomation(values);
    push(`${t('toast.automationAdded')} — ${created.name}`, 'success');
    setFormOpen(false);
    setSearchParams({});
    reload();
  }
  async function handleEdit(values: AutomationFormValues) {
    if (!editTarget) return;
    await dataService.updateAutomation(editTarget.id, values);
    push(t('toast.automationUpdated'), 'success');
    setEditTarget(null);
    reload();
  }
  async function duplicate(a: Automation) {
    await dataService.duplicateAutomation(a.id);
    push(t('toast.automationDuplicated'), 'success');
    reload();
  }
  async function togglePause(a: Automation) {
    await dataService.toggleAutomationPause(a.id);
    reload();
  }
  async function archive(a: Automation) {
    await dataService.archiveAutomation(a.id);
    push(t('toast.automationArchived'), 'warn');
    reload();
  }
  async function confirmDelete() {
    if (!deleteTarget) return;
    await dataService.deleteAutomation(deleteTarget.id);
    push(t('toast.demoRecordDeleted'), 'error');
    setDeleteTarget(null);
    reload();
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>{t('nav.automations')}</h1>
        <button className="btn-primary" onClick={() => setFormOpen(true)}>+ {t('automationForm.newTitle')}</button>
      </div>
      <p style={{ color: 'var(--text-2)', marginTop: -8, marginBottom: 16 }}>{t('automations.subtitle')}</p>

      <div className="card" style={{ padding: 8 }}>
        {automations.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>{t('automations.empty')}</div>
        ) : (
          <div className="table-scroll">
            <table className="dt">
              <thead>
                <tr>
                  <th>{t('automationForm.name')}</th>
                  <th>{t('automationForm.trigger')}</th>
                  <th>{t('automationForm.ownerAgent')}</th>
                  <th>{t('automationForm.approvalLevel')}</th>
                  <th>{t('automations.col.status')}</th>
                  <th>{t('automations.col.lastRun')}</th>
                  <th>{t('automations.col.nextRun')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {automations.map((a) => (
                  <tr key={a.id}>
                    <td>{a.name}<div style={{ fontSize: 11, color: 'var(--text-3)' }}>{a.description}</div></td>
                    <td style={{ fontSize: 12 }}>{a.trigger}</td>
                    <td>{a.ownerAgent ? (t(getDepartmentDefinition(a.ownerAgent)?.nameKey ?? '') || a.ownerAgent) : t('automations.unassigned')}</td>
                    <td><span className={`badge ${a.approvalLevel === 3 ? 'crit' : a.approvalLevel === 2 ? 'warn' : 'info'}`}>{t(`permission.level${a.approvalLevel}`)}</span></td>
                    <td><StatusBadge entity="automation" statusKey={a.status} /></td>
                    <td style={{ fontSize: 12 }}>{a.lastRun ?? '—'}</td>
                    <td style={{ fontSize: 12 }}>{a.nextRun ?? '—'}</td>
                    <td style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button onClick={() => setEditTarget(a)}>{t('rowMenu.edit')}</button>
                      <button onClick={() => duplicate(a)}>{t('automations.duplicate')}</button>
                      <button onClick={() => togglePause(a)}>{a.status === 'paused' ? t('agentForm.resume') : t('agentForm.pause')}</button>
                      <button style={{ color: 'var(--warn)' }} onClick={() => archive(a)}>{t('rowMenu.archive')}</button>
                      {a.isPreviewCreated && <button style={{ color: 'var(--crit)' }} onClick={() => setDeleteTarget(a)}>{t('rowMenu.deleteDemo')}</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {formOpen && (
        <AutomationFormDrawer
          defaultApprovalLevel={settings?.automationRules.defaultApprovalLevel}
          onClose={() => { setFormOpen(false); setSearchParams({}); }}
          onSubmit={handleCreate}
        />
      )}
      {editTarget && <AutomationFormDrawer automation={editTarget} onClose={() => setEditTarget(null)} onSubmit={handleEdit} />}

      <ConfirmModal
        open={!!deleteTarget}
        title={t('confirm.deleteAutomationTitle')}
        recordName={deleteTarget?.name}
        description={t('confirm.deleteAutomationDesc')}
        reversible={false}
        tone="destructive"
        confirmLabel={t('rowMenu.deleteDemo')}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
